import { useState, type FormEvent } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { AccountsAdminApi } from '../adminApi.js'
import type { ClientsAdminApi } from '../../clients/index.js'
import type { FacilitiesAdminApi } from '../../facilities/index.js'
import type { AdminClientSummary } from '../../clients/index.js'
import type { InvitationResult } from '../index.js'
import { Modal, Skeleton } from '../../core/index.js'
import { MailPlus, Plus } from 'lucide-react'
import { InvitationHandOffBanner } from '../components/InviteOperatorModal.js'
import { ClientSelect, INPUT_CLASS, LINK_BUTTON, useActiveClients, useClientFacilities } from './formParts.js'
import { Field, FormError } from '../../core/index.js'
import { Button } from '../../core/index.js'
import { PhoneInput } from '../../core/index.js'

/** The admin calls this screen uses. */
type AdminApi = AccountsAdminApi & ClientsAdminApi & FacilitiesAdminApi

const FORM_ID = 'new-facility-administrator-form'

interface AdministratorForm {
  firstName: string
  lastName: string
  email: string
  phone: string
  facilityIds: readonly string[]
}

const EMPTY_FORM: AdministratorForm = { firstName: '', lastName: '', email: '', phone: '', facilityIds: [] }

interface NewFacilityAdministratorModalProps {
  api: AdminApi
  /** The client the administrator belongs to, when the form is opened from one; otherwise the admin picks. */
  client?: AdminClientSummary
  backLabel?: string
  onClose: () => void
}

/**
 * Invites a facility administrator - an individual login scoped to some of
 * a client's facilities - through
 * `POST /admin/clients/{clientId}/facility-administrators/invitations`
 * (invitations plan phase 2): the login is made at once, switched off, and
 * the person is emailed a link and a code to set their own password with.
 */
export function NewFacilityAdministratorModal({ api, client: fixedClient, backLabel, onClose }: NewFacilityAdministratorModalProps) {
  const queryClient = useQueryClient()
  const clientsQuery = useActiveClients(api)
  const [registrationId, setRegistrationId] = useState(fixedClient?.registrationId ?? '')
  const client = fixedClient ?? clientsQuery.clients.find((candidate) => candidate.registrationId === registrationId) ?? null
  const clientId = client?.clientId ?? null
  const facilitiesQuery = useClientFacilities(api, clientId)
  const [form, setForm] = useState<AdministratorForm>(EMPTY_FORM)
  const [created, setCreated] = useState<InvitationResult | null>(null)

  const mutation = useMutation({
    mutationFn: ({ phone, ...input }: AdministratorForm) => api.inviteFacilityAdministrator(clientId ?? '', { ...input, facilityIds: [...input.facilityIds], ...(phone.trim() ? { phone: phone.trim() } : {}) }),
    onSuccess: async (result) => {
      setCreated(result)
      await queryClient.invalidateQueries({ queryKey: ['admin', 'facility-administrators'] })
      await queryClient.invalidateQueries({ queryKey: ['admin', 'facility-administrator-invitations'] })
    },
  })

  function update(changes: Partial<AdministratorForm>): void {
    setForm((current) => ({ ...current, ...changes }))
  }

  function toggleFacility(facilityId: string): void {
    setForm((current) => ({
      ...current,
      facilityIds: current.facilityIds.includes(facilityId) ? current.facilityIds.filter((existing) => existing !== facilityId) : [...current.facilityIds, facilityId],
    }))
  }

  function handleSubmit(event: FormEvent): void {
    event.preventDefault()
    if (!clientId || form.facilityIds.length === 0) {
      return
    }
    mutation.mutate({ firstName: form.firstName.trim(), lastName: form.lastName.trim(), email: form.email.trim(), phone: form.phone, facilityIds: form.facilityIds })
  }

  function createAnother(): void {
    setCreated(null)
    mutation.reset()
    setForm(EMPTY_FORM)
  }

  const canSubmit = clientId !== null && form.firstName.trim() !== '' && form.lastName.trim() !== '' && form.email.trim() !== '' && form.facilityIds.length > 0 && !mutation.isPending
  const facilityNames = created?.invitation.about.facilityNames ?? []

  return (
    <Modal
      eyebrow="Invite a facility administrator"
      title={created ? 'Invitation sent' : 'Invite a facility administrator'}
      subtitle={client?.organizationName}
      backLabel={backLabel}
      canClose={!mutation.isPending}
      onClose={onClose}
      footer={
        created ? (
          <div className="flex items-center gap-4">
            <Button icon={Plus} onClick={createAnother}>
              Invite another
            </Button>
            <button type="button" onClick={onClose} className={LINK_BUTTON}>
              Done
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-4">
            <Button icon={MailPlus} type="submit" form={FORM_ID} disabled={!canSubmit}>
              {mutation.isPending ? 'Inviting…' : 'Invite facility administrator'}
            </Button>
            <button type="button" onClick={onClose} disabled={mutation.isPending} className={LINK_BUTTON}>
              Cancel
            </button>
          </div>
        )
      }
    >
      {created ? (
        <div className="bg-canvas p-6 text-sm text-ink space-y-3">
          <p>
            <span className="font-bold">
              {created.invitation.firstName} {created.invitation.lastName}
            </span>{' '}
            has been invited as <span className="font-mono text-xs">{created.invitation.email}</span> to manage {facilityNames.join(', ')} - those facilities only, not the rest of the client. They set their own password from the invitation.
          </p>
          <InvitationHandOffBanner result={created} onDismiss={() => setCreated({ ...created, handOff: null })} />
        </div>
      ) : (
        <form id={FORM_ID} onSubmit={handleSubmit} className="bg-canvas p-6 space-y-5">
          {!fixedClient && (
            <Field label="Client" htmlFor="new-administrator-client" required>
              <ClientSelect
                id="new-administrator-client"
                clients={clientsQuery.clients}
                isLoading={clientsQuery.isLoading}
                value={registrationId}
                onChange={(next) => {
                  setRegistrationId(next)
                  update({ facilityIds: [] })
                }}
              />
            </Field>
          )}
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="First name" htmlFor="new-administrator-first-name" required>
              <input id="new-administrator-first-name" className={INPUT_CLASS} value={form.firstName} onChange={(event) => update({ firstName: event.target.value })} required autoFocus />
            </Field>
            <Field label="Last name" htmlFor="new-administrator-last-name" required>
              <input id="new-administrator-last-name" className={INPUT_CLASS} value={form.lastName} onChange={(event) => update({ lastName: event.target.value })} required />
            </Field>
          </div>
          <Field label="Login email" htmlFor="new-administrator-email" required hint="Their username. Must be unique across the whole platform.">
            <input id="new-administrator-email" type="email" className={INPUT_CLASS} value={form.email} onChange={(event) => update({ email: event.target.value })} required autoComplete="off" />
          </Field>
          <Field label="Phone" htmlFor="new-administrator-phone" hint="Optional. Shown with their name and email on the facility's dashboard, so the operator can reach them.">
            <PhoneInput id="new-administrator-phone" className={INPUT_CLASS} value={form.phone} onChange={(phone) => update({ phone })} autoComplete="off" />
          </Field>
          <fieldset>
            <legend className="block text-xs font-bold text-ink-mid mb-2">
              Facilities they manage <span className="font-normal text-coral">At least one</span>
            </legend>
            {clientId === null ? (
              <p className="text-[11px] text-ink-mid">Choose a client first.</p>
            ) : facilitiesQuery.isLoading ? (
              <Skeleton label="Loading facilities" />
            ) : facilitiesQuery.facilities.length === 0 ? (
              <p className="text-[11px] text-coral font-bold">This client has no facilities yet, and an administrator must manage at least one. Its operator registers facilities from their own dashboard.</p>
            ) : (
              <div className="space-y-2">
                {facilitiesQuery.facilities.map((facility) => (
                  <label key={facility.facilityId} className="flex items-start gap-2 text-sm text-ink cursor-pointer">
                    <input type="checkbox" className="mt-0.5" checked={form.facilityIds.includes(facility.facilityId)} onChange={() => toggleFacility(facility.facilityId)} />
                    <span>{facility.name}</span>
                  </label>
                ))}
              </div>
            )}
          </fieldset>
          <p className="text-[11px] text-ink-mid">Nobody sees a password: they are emailed a link and a code, good for seven days, and choose their own.</p>
          <FormError error={mutation.error} fallback="The facility administrator could not be invited." />
        </form>
      )}
    </Modal>
  )
}
